import { ChangeDetectionStrategy, Component, Input, OnDestroy, Signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { QuickBetConfig } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserService } from '@frontend/sports/user/feature';
import { LocalStoreService, UserEvent, UserLoggingInEvent } from '@frontend/vanilla/core';
import { Store, createSelector } from '@ngrx/store';
import { DispatcherEvent } from 'packages/sports/web/app/src/event-details-common/event-details-common.models';
import { AdaptiveLayoutService } from 'packages/sports/web/app/src/layout/adaptive-layout.service';
import { uiStateSelector } from 'packages/sports/web/app/src/ui-manager/ui-manager.state';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { combineLatest, distinctUntilChanged, filter, finalize, map, pairwise, take } from 'rxjs';

import { BottomOffsetService } from '../../../../../web/app/src/layout/bottom-offset.service';
import { IBetslipRootState } from '../../../base/store/state';
import { QuickBetActions } from '../quick-bet-actions';
import { quickBetUiStateSelector, selectIsQuickBetInOverlay, selectQuickBetPicks, showQuickBetHelpSelector } from '../quick-bet.selectors';
import { QuickBetStore, QuickBetUiState } from '../quick-bet.state';
import { QuickBetComboPreventionService } from '../services/quick-bet-combo-prevention.service';
import { QuickBetRendererService } from '../services/quick-bet-renderer.service';
import { QuickBetService } from '../services/quick-bet.service';

export const selectIsVisible = createSelector(uiStateSelector, (uiState) => uiState.states.some((state) => state === UiStates.QuickBet));

// bottom nav + betbar heights
const bottomOffset = 50 + 56;

@Component({
    selector: 'bs-digital-quick-bet-container',
    templateUrl: './quick-bet-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet-container.component.scss'],
    providers: [QuickBetComboPreventionService],
    host: {
        '[style.height.px]': 'hostHeight()',
    },
    standalone: false,
})
export class QuickBetContainerComponent implements OnDestroy {
    @Input() oddsDisplayFormat: OddsDisplayFormat;

    shouldRenderQuickBet = computed(() => (this.quickBetConfig.isHeaderEnabled ? this.quickBetRendererService.summaryLoaded : true));
    protected loadingState: Signal<boolean>;
    protected placeState: Signal<boolean>;
    protected successState: Signal<boolean>;
    protected hostHeight = computed<number>(() => this.bottomOffsetService.bottomOffset() - bottomOffset);
    protected isHelpVisible: Signal<boolean>;
    protected canShowRewardTokensOnboarding: Signal<boolean>;
    protected isVisible: Signal<boolean>;
    protected inOverlay: Signal<boolean>;

    constructor(
        private routerEventsService: RouterEventsService,
        private userService: UserService,
        private adaptiveLayoutService: AdaptiveLayoutService,
        private dispatcher: DispatcherService,
        private quickBetService: QuickBetService,
        private localStoreService: LocalStoreService,
        private store: Store<IBetslipRootState>,
        private quickBetComboPreventionService: QuickBetComboPreventionService,
        private quickBetRendererService: QuickBetRendererService,
        private quickBetConfig: QuickBetConfig,
        private bottomOffsetService: BottomOffsetService,
    ) {
        const quickBetUiState = this.store.selectSignal(quickBetUiStateSelector);
        this.loadingState = computed(() => quickBetUiState() === QuickBetUiState.Loading);
        this.placeState = computed(() => quickBetUiState() === QuickBetUiState.Place);
        this.successState = computed(() => quickBetUiState() === QuickBetUiState.Success);
        const helpState = this.store.selectSignal(showQuickBetHelpSelector);
        this.isHelpVisible = computed(() => helpState().isVisible && !helpState().wasShown);
        this.isVisible = this.store.selectSignal(selectIsVisible);
        const shouldShowHelpState = computed(() => {
            const quickBetStore = this.localStoreService.get<QuickBetStore>('quickBet') || { helpShown: false };

            return !helpState().isVisible && !helpState().wasShown && quickBetStore.helpShown;
        });
        const quickBetPicks = this.store.selectSignal(selectQuickBetPicks);
        this.canShowRewardTokensOnboarding = computed(() => {
            const hasNewCustomerOffer = quickBetPicks().some((pick) => pick.isNewCustomerOfferPick) ?? false;

            return !hasNewCustomerOffer && shouldShowHelpState();
        });
        this.inOverlay = this.store.selectSignal(selectIsQuickBetInOverlay);

        this.initSubscriptions();
    }

    private initSubscriptions(): void {
        if (this.quickBetService.isQuickBetEnabled) {
            this.createRouteChangeSubscription();
            this.createQuickBetLayoutChangeSubscription();
            this.createEventsSubscriptions();
            this.createContentVisibilityChangeSubscription();
            this.quickBetComboPreventionService.init();
        }
    }

    private createEventsSubscriptions(): void {
        this.dispatcher
            .on<boolean>(DispatcherEvent.EventDetails.Expanded)
            .pipe(takeUntilDestroyed())
            .subscribe((isExpanded) => this.quickBetService.eventDetailsExpandHandler(isExpanded));

        this.userService.events.pipe(takeUntilDestroyed()).subscribe((event: UserEvent) => {
            if (event instanceof UserLoggingInEvent && this.quickBetService.isQuickBetVisible) {
                this.store.dispatch(QuickBetActions.keepOpen({ open: true }));
            }
        });
    }

    private createQuickBetLayoutChangeSubscription(): void {
        combineLatest([this.adaptiveLayoutService.stateChange$])
            .pipe(
                map(([state]) => state.quickBetActive),
                distinctUntilChanged(),
                takeUntilDestroyed(),
            )
            .subscribe((active) => this.quickBetService.toggleQuickBetVisibility(active));
    }

    private createRouteChangeSubscription(): void {
        this.isValidInitialRoute();
        this.routerEventsService.currentActivationEnd
            .pipe(
                filter(isDefined),
                pairwise(),
                finalize(() => {
                    if (this.quickBetService.isQuickBetVisible) {
                        this.store.dispatch(QuickBetActions.keepOpen({ open: true }));
                    }
                }),
                takeUntilDestroyed(),
            )
            .subscribe(([previous, current]) => this.quickBetService.activationEndHandler(previous, current));
    }

    private isValidInitialRoute(): void {
        this.routerEventsService.currentActivationEnd
            .pipe(
                take(1),
                map((event) => this.quickBetService.initialRouteCheck(event)),
            )
            .subscribe();
    }

    private createContentVisibilityChangeSubscription(): void {
        this.store
            .select(selectIsVisible)
            .pipe(
                distinctUntilChanged(),
                filter((isVisible) => !isVisible),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.bottomOffsetService.resetBottomOffset());
    }

    ngOnDestroy(): void {
        //reset the calculation finished to prevent flickering at next opening of qb
        this.quickBetRendererService.summaryCalculationFinished = false;
        this.bottomOffsetService.resetBottomOffset();
    }

    protected sizeChanged(resizeEvent: { width: number; height: number }) {
        this.bottomOffsetService.setBottomOffset(resizeEvent.height);
    }
}
